import { Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import PersonaSelector from '@/screens/PersonaSelector';
import NetworkView from '@/screens/NetworkView';
import StudioView from '@/screens/StudioView';
import OperatorQueue from '@/screens/OperatorQueue';
import CustomerRecord from '@/screens/CustomerRecord';
import MessengerInbox from '@/screens/MessengerInbox';
import Nurture from '@/screens/Nurture';
import StudioCalendar from '@/screens/StudioCalendar';
import SelfBookLinkGenerator from '@/screens/SelfBookLinkGenerator';
import InChairRebooking from '@/screens/InChairRebooking';
import DepositCollection from '@/screens/DepositCollection';
import ReportingDashboard from '@/screens/ReportingDashboard';
import LeakReportingScreen from '@/screens/reporting/LeakReportingScreen';
import InternalComms from '@/screens/InternalComms';
import FutureReadinessCalendar from '@/screens/FutureReadinessCalendar';
import Settings from '@/screens/Settings';
import ArtistDashboard from '@/screens/ArtistDashboard';
import ApprenticeDashboard from '@/screens/ApprenticeDashboard';
import OwnerDashboard from '@/screens/OwnerDashboard';
import Leaderboard from '@/screens/Leaderboard';
import LeaderboardTV from '@/screens/LeaderboardTV';
import PlaytestConsole from '@/screens/PlaytestConsole';
import ControlConsole from '@/screens/ControlConsole';
import ConfirmationsRoute from '@/screens/ConfirmationsRoute';
import ArtistAvailability from '@/screens/ArtistAvailability';
import DepositsLedger from '@/screens/DepositsLedger';
import ArtistManagement from '@/screens/ArtistManagement';
import FloorManager from '@/screens/FloorManager';
import FloorBooking from '@/screens/FloorBooking';
import PayoutsCash from '@/screens/PayoutsCash';
import Reconciliation from '@/screens/Reconciliation';
import Calculations from '@/screens/Calculations';
import Customers from '@/screens/Customers';
import DetailShowcase from '@/screens/DetailShowcase';
import HowItWorks from '@/screens/HowItWorks';
import SelfBookPage from '@/screens/customer-flow/SelfBookPage';
import TopupPayPage from '@/screens/customer-flow/TopupPayPage';
import WaiverSignPage from '@/screens/customer-flow/WaiverSignPage';
import DepositPayPage from '@/screens/customer-flow/DepositPayPage';
import AppShell from '@/app/AppShell';
import Sweeper from '@/screens/Sweeper';
import OpsWorklist from '@/screens/OpsWorklist';
import { useSessionStore } from '@/state/sessionStore';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { startSimulation, stopSimulation } from '@/events/simulationEngine';
import { canAccessFinanceScreens, canAccessReporting, canOpenReportingScreen } from '@/domain/permissions';

export default function App() {
  const initialised = useBrainStore((s) => s.initialised);
  const loadFixtures = useBrainStore((s) => s.loadFixtures);
  const persona = useSessionStore((s) => s.persona);

  useEffect(() => {
    if (!initialised) {
      loadFixtures();
    }
  }, [initialised, loadFixtures]);

  useEffect(() => {
    if (!initialised) return;
    startSimulation();
    return () => stopSimulation();
  }, [initialised]);

  // Expose test-only window APIs in dev builds.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const w = window as typeof window & {
      __sim?: unknown;
      __brain?: unknown;
      __session?: unknown;
      __ui?: unknown;
    };
    import('@/events/simulationEngine').then((mod) => {
      w.__sim = mod.simulationApi;
    });
    w.__brain = useBrainStore;
    w.__session = useSessionStore;
    import('@/state/uiStore').then((mod) => {
      w.__ui = mod.useUIStore;
    });
    import('@/sound/audioSystem').then((mod) => {
      (w as typeof w & { __getAudioCalls?: () => string[] }).__getAudioCalls =
        mod.getAudioCalls;
      (w as typeof w & { __resetAudioCalls?: () => void }).__resetAudioCalls =
        mod.resetAudioCalls;
    });
  }, []);

  // Test-only sim-clock control for the SERVED (production) build. The demo clock
  // is frozen at DEMO_NOW for real users (simulationStore) and the DEV window
  // hooks above are stripped from the prod build — so time-dependent lifecycle
  // flows (e.g. the confirmation cadence) are unreachable in an E2E run against
  // dist. This exposes a minimal `window.__e2e` clock control GATED on an
  // explicit localStorage marker (`__e2e === '1'`) that a real user never sets;
  // the E2E harness arms it via Playwright `addInitScript` before load. Absent
  // and inert without the marker, so the served demo stays frozen at DEMO_NOW.
  // Deliberately separate from the DEV-only hooks above (never widen that guard
  // to prod — that would expose __sim/__brain to every visitor).
  useEffect(() => {
    let armed = false;
    try {
      armed = localStorage.getItem('__e2e') === '1';
    } catch {
      armed = false;
    }
    if (!armed) return;

    const recompute = () => {
      try {
        useBrainStore.getState().recomputeTodayStats();
      } catch {
        // brain not hydrated yet — a later load/mutation recomputes.
      }
    };
    const persist = (iso: string) => {
      try {
        localStorage.setItem('__e2e_clock', iso);
      } catch {
        // storage unavailable — the in-memory clock still applies this session.
      }
    };

    // Re-apply a previously pinned clock so it survives full-page navigations
    // (every reload boots at DEMO_NOW; this restores the test's chosen instant).
    try {
      const stored = localStorage.getItem('__e2e_clock');
      if (stored) {
        useSimulationStore.getState().setClock(stored);
        recompute();
      }
    } catch {
      // storage unavailable — leave the clock frozen at DEMO_NOW.
    }

    (window as typeof window & { __e2e?: unknown }).__e2e = {
      setClock(iso: string) {
        useSimulationStore.getState().setClock(iso);
        recompute();
        persist(iso);
      },
      stepDays(delta: number) {
        useSimulationStore.getState().stepDay(delta);
        persist(useSimulationStore.getState().clockIso);
      },
      reset() {
        useSimulationStore.getState().resetClock();
        try {
          localStorage.removeItem('__e2e_clock');
        } catch {
          // best-effort — resetClock already restored DEMO_NOW in memory.
        }
      },
    };
  }, []);

  // Route persona-less users to the splash; everything else mounts under AppShell.
  return (
    <Routes>
      <Route path="/" element={<PersonaSelector />} />
      <Route path="/customer-book/:linkToken" element={<SelfBookPage />} />
      <Route path="/customer-topup/:planId" element={<TopupPayPage />} />
      <Route path="/customer-waiver/:waiverId" element={<WaiverSignPage />} />
      <Route path="/customer-deposit/:depositId" element={<DepositPayPage />} />
      <Route
        path="/leaderboard/tv/:studioId"
        element={persona ? <LeaderboardTV /> : <Navigate to="/" />}
      />
      <Route path="/console" element={<ControlConsole />} />
      <Route element={<AppShell />}>
        <Route path="/network" element={persona ? <NetworkView /> : <Navigate to="/" />} />
        <Route path="/studio/:studioId" element={persona ? <StudioView /> : <Navigate to="/" />} />
        <Route path="/queue" element={persona ? <OperatorQueue /> : <Navigate to="/" />} />
        {/* Ops and the Sweeper seat (75n6) work the Sweeper. */}
        <Route
          path="/sweeper"
          element={persona === 'georgina' || persona === 'sweeper' ? <Sweeper /> : <Navigate to="/" />}
        />
        <Route path="/ops" element={persona === 'georgina' ? <OpsWorklist /> : <Navigate to="/" />} />
        <Route
          path="/customer/:customerId"
          element={persona ? <CustomerRecord /> : <Navigate to="/" />}
        />
        <Route
          path="/conversations"
          element={persona ? <MessengerInbox /> : <Navigate to="/" />}
        />
        <Route path="/nurture" element={persona ? <Nurture /> : <Navigate to="/" />} />
        <Route
          path="/calendar/:studioId"
          element={persona ? <StudioCalendar /> : <Navigate to="/" />}
        />
        <Route
          path="/self-book-generator"
          element={persona ? <SelfBookLinkGenerator /> : <Navigate to="/" />}
        />
        <Route
          path="/in-chair-rebooking/:bookingId"
          element={persona ? <InChairRebooking /> : <Navigate to="/" />}
        />
        <Route
          path="/deposit/:depositId"
          element={persona ? <DepositCollection /> : <Navigate to="/" />}
        />
        <Route path="/deposits" element={persona ? <DepositsLedger /> : <Navigate to="/" />} />
        {/* /approvals retired (tv-i0b): the no-deposit override queue now lives on
            the Ops worklist Overrides tab. Keep the path so console + deep-links
            do not 404 - redirect to /ops. */}
        <Route path="/approvals" element={<Navigate to="/ops" replace />} />
        <Route path="/artists" element={persona ? <ArtistManagement /> : <Navigate to="/" />} />
        <Route path="/floor" element={persona ? <FloorManager /> : <Navigate to="/" />} />
        <Route path="/floor/:bookingId" element={persona ? <FloorBooking /> : <Navigate to="/" />} />
        <Route path="/payouts" element={persona ? <PayoutsCash /> : <Navigate to="/" />} />
        <Route
          path="/reconciliation"
          element={
            persona && canAccessFinanceScreens(persona) ? (
              <Reconciliation />
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/calculations"
          element={
            persona && canAccessFinanceScreens(persona) ? (
              <Calculations />
            ) : (
              <Navigate to="/" />
            )
          }
        />
        {/* Settlement folded into Reconcile (amendment 11); keep the old path as a redirect. */}
        <Route path="/settlement" element={<Navigate to="/reconciliation" replace />} />
        <Route
          path="/reporting"
          element={persona && canOpenReportingScreen(persona) ? <LeakReportingScreen /> : <Navigate to="/" />}
        />
        <Route
          path="/reporting-legacy"
          element={persona && canAccessReporting(persona) ? <ReportingDashboard /> : <Navigate to="/" />}
        />
        <Route path="/comms" element={persona ? <InternalComms /> : <Navigate to="/" />} />
        <Route
          path="/future-readiness"
          element={persona ? <FutureReadinessCalendar /> : <Navigate to="/" />}
        />
        <Route path="/settings" element={persona ? <Settings /> : <Navigate to="/" />} />
        <Route
          path="/confirmations"
          element={persona ? <ConfirmationsRoute /> : <Navigate to="/" />}
        />
        <Route
          path="/availability"
          element={persona ? <ArtistAvailability /> : <Navigate to="/" />}
        />
        <Route path="/customers" element={persona ? <Customers /> : <Navigate to="/" />} />
        <Route path="/artist/:artistId" element={persona ? <ArtistDashboard /> : <Navigate to="/" />} />
        <Route
          path="/apprentice/:apprenticeId"
          element={persona ? <ApprenticeDashboard /> : <Navigate to="/" />}
        />
        <Route path="/owner" element={persona ? <OwnerDashboard /> : <Navigate to="/" />} />
        <Route
          path="/leaderboard"
          element={persona ? <Leaderboard /> : <Navigate to="/" />}
        />
        <Route
          path="/playtest"
          element={persona ? <PlaytestConsole /> : <Navigate to="/" />}
        />
        <Route path="/showcase" element={persona ? <DetailShowcase /> : <Navigate to="/" />} />
        <Route path="/how-it-works" element={persona ? <HowItWorks /> : <Navigate to="/" />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Suppress unused warning for the simulation store; consumed via window.__sim.
void useSimulationStore;
